import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, Alert, Button, Stack, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel,
} from "@mui/material";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", accent: "#637cdb", accentDark: "#3F6AC0" };

export function glassCardSx(isDark) {
  return {
    borderRadius: "16px", overflow: "hidden",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
    bgcolor: isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)",
    backdropFilter: "blur(20px) saturate(140%)",
    WebkitBackdropFilter: "blur(20px) saturate(140%)",
    boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.4)" : "0 24px 80px rgba(10,14,39,0.08)",
  };
}

export function tableHeadSx(isDark) {
  return {
    "& th": {
      fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 11,
      color: isDark ? "#94a3b8" : "#64748b",
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}`,
    },
  };
}

export function tableRowSx(isDark) {
  return { "& td": { borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(10,14,39,0.05)"}` } };
}

const filterInputSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };

// ── Pending Registrations ────────────────────────────────────────────────
// Patients who started a telehealth/other-lab order and never paid (see the
// resumable-registration fix on /register) — previously had zero visibility
// anywhere in the admin UI.
export function PendingRegistrationsPanel({ admin, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${admin?.token}` };
        const res = await axios.get(`${API_BASE}/admin/pending-registrations`, { headers });
        setData(res.data.pending_registrations || []);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load pending registrations.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <CircularProgress size={20} sx={{ color: BRAND.accent }} />;
  if (error) return <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>{error}</Alert>;
  if (!data || data.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
        {data.length} patient{data.length === 1 ? "" : "s"} started registration but never completed payment —
        their account exists but nothing was ever purchased.
      </Alert>
      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Stuck On</TableCell>
              <TableCell>Started</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((u) => (
              <TableRow key={u.id} hover sx={tableRowSx(isDark)}>
                <TableCell>{u.name || "—"}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.phone || "—"}</TableCell>
                <TableCell>
                  {u.pending_kind === "telehealth" && <Chip size="small" label="Telehealth checkout" color="warning" />}
                  {u.pending_kind === "other_lab" && <Chip size="small" label="Other-lab checkout" color="warning" />}
                  {!u.pending_kind && <Chip size="small" label="Unknown" />}
                </TableCell>
                <TableCell>{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Telehealth Staff list + actions ──────────────────────────────────────
export function StaffListTable({ admin, isDark, refreshKey }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const headers = { Authorization: `Bearer ${admin?.token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/telehealth-staff`, { headers });
      setStaff(res.data.staff || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load staff accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const toggleActive = async (u) => {
    setBusyId(u.id);
    try {
      await axios.post(`${API_BASE}/admin/users/${u.id}/${u.is_active ? "deactivate" : "reactivate"}`, {}, { headers });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update this account.");
    } finally {
      setBusyId(null);
    }
  };

  const resetCredentials = async (u) => {
    setBusyId(u.id);
    try {
      await axios.post(`${API_BASE}/admin/users/${u.id}/reset-credentials`, {}, { headers });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset credentials.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <CircularProgress size={20} sx={{ color: BRAND.accent }} />;

  return (
    <Box sx={{ mb: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
        <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
          Existing Staff Accounts
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((u) => (
              <TableRow key={u.id} hover sx={tableRowSx(isDark)}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.role === "logistics" ? "Logistics" : "Telehealth Clinician"}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  <Chip size="small" label={u.is_active ? "Active" : "Deactivated"} color={u.is_active ? "success" : "default"} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" disabled={busyId === u.id}
                      onClick={() => resetCredentials(u)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                      Reset Password
                    </Button>
                    <Button size="small" variant="outlined" color={u.is_active ? "error" : "success"} disabled={busyId === u.id}
                      onClick={() => toggleActive(u)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                      {u.is_active ? "Deactivate" : "Reactivate"}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {staff.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "#94a3b8" }}>No staff accounts yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Stuck claims — force unclaim ─────────────────────────────────────────
export function ClaimedOrdersTable({ admin, isDark, claimedOrders, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const headers = { Authorization: `Bearer ${admin?.token}` };

  const forceUnclaim = async (orderId) => {
    setBusyId(orderId);
    setError(null);
    try {
      await axios.post(`${API_BASE}/admin/telehealth-review/${orderId}/force-unclaim`, {}, { headers });
      onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to unclaim this order.");
    } finally {
      setBusyId(null);
    }
  };

  if (!claimedOrders || claimedOrders.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
        <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
          Currently Claimed — Needs Attention?
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>Customer ID</TableCell>
              <TableCell>Child</TableCell>
              <TableCell>Claimed By</TableCell>
              <TableCell>Claimed At</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {claimedOrders.map((o) => (
              <TableRow key={o.order_id} hover sx={tableRowSx(isDark)}>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{o.customer_id}</TableCell>
                <TableCell>{o.child_name}</TableCell>
                <TableCell>Dr. {o.reviewing_clinician_name}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{o.claimed_at ? new Date(o.claimed_at).toLocaleString() : "—"}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" color="error" disabled={busyId === o.order_id}
                    onClick={() => forceUnclaim(o.order_id)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                    Force Unclaim
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Notification failures ────────────────────────────────────────────────
const NOTIF_KIND_LABEL = {
  patient_credentials: "Patient credentials", clinician_credentials: "Clinician credentials",
  payment_request: "Payment request", payment_confirmation: "Payment confirmation",
  password_reset: "Password reset", monthly_digest: "Monthly digest",
};

export function NotificationsPanel({ admin, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${admin?.token}` };
        const res = await axios.get(`${API_BASE}/admin/notifications`, { headers });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load notifications.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <CircularProgress size={24} sx={{ color: BRAND.accent }} />;
  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  if (!data) return null;

  const filtered = data.notifications.filter((n) => {
    if (statusFilter !== "all" && n.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!`${n.recipient} ${n.kind}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box sx={{ ...glassCardSx(isDark), flex: 1, minWidth: 140, p: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 0.5 }}>
            Total logged (last 500)
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>{data.notifications.length}</Typography>
        </Box>
        <Box sx={{ ...glassCardSx(isDark), flex: 1, minWidth: 140, p: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 0.5 }}>
            Failed
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: data.failed_count > 0 ? "#dc2626" : (isDark ? "#f1f5f9" : BRAND.navy) }}>
            {data.failed_count}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <TextField size="small" label="Search recipient / kind" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240, ...filterInputSx }} />
        <FormControl size="small" sx={{ minWidth: 150, ...filterInputSx }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>When</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((n) => (
              <TableRow key={n._id} hover sx={tableRowSx(isDark)}>
                <TableCell sx={{ fontSize: 12.5 }}>{n.created_at ? new Date(n.created_at).toLocaleString() : "—"}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{NOTIF_KIND_LABEL[n.kind] || n.kind}</TableCell>
                <TableCell sx={{ fontSize: 12.5, textTransform: "uppercase" }}>{n.channel}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{n.recipient}</TableCell>
                <TableCell>
                  <Chip size="small" label={n.status === "sent" ? "Sent" : "Failed"} color={n.status === "sent" ? "success" : "error"} />
                </TableCell>
                <TableCell sx={{ fontSize: 11.5, color: "#dc2626", maxWidth: 280 }}>{n.error || "—"}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8" }}>No notifications match.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Per-user detail dialog ────────────────────────────────────────────────
export function UserDetailDialog({ open, onClose, userId, admin, isDark, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${admin?.token}` };

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/admin/users/${userId}`, { headers });
      setDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load this user.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const resetCredentials = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/admin/users/${userId}/reset-credentials`, {}, { headers });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset credentials.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/admin/users/${userId}/${detail.is_active ? "deactivate" : "reactivate"}`, {}, { headers });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update this account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: isDark ? "rgba(17,24,39,0.97)" : "#ffffff", backgroundImage: "none" } }}>
      <DialogTitle sx={{ fontWeight: 800, color: isDark ? "#f1f5f9" : "#0a0e27" }}>User Detail</DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress size={20} sx={{ color: BRAND.accent }} />}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {detail && !loading && (
          <>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{detail.name}</Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mb: 1.5 }}>{detail.email} · {detail.phone || "no phone"}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip size="small" label={detail.role} />
              <Chip size="small" label={detail.is_active ? "Active" : "Deactivated"} color={detail.is_active ? "success" : "default"} />
              {detail.registration_path
                ? <Chip size="small" label={`Path: ${detail.registration_path}`} color="info" />
                : detail.role === "patient" && <Chip size="small" label="Pending registration" color="warning" />}
              {detail.subscription_optin && <Chip size="small" label="$0.99/mo subscriber" color="success" />}
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
              <Button size="small" variant="outlined" disabled={busy} onClick={resetCredentials} sx={{ textTransform: "none", borderRadius: "8px" }}>
                Resend / Reset Credentials
              </Button>
              <Button size="small" variant="outlined" color={detail.is_active ? "error" : "success"} disabled={busy} onClick={toggleActive}
                sx={{ textTransform: "none", borderRadius: "8px" }}>
                {detail.is_active ? "Deactivate Account" : "Reactivate Account"}
              </Button>
            </Stack>

            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
              Payment Requests ({detail.payment_requests.length})
            </Typography>
            {detail.payment_requests.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mb: 2 }}>None.</Typography>
            ) : (
              <Box sx={{ mb: 2 }}>
                {detail.payment_requests.map((pr) => (
                  <Box key={pr._id} sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, py: 0.4 }}>
                    <span>{pr.label}</span><b>{pr.status}</b>
                  </Box>
                ))}
              </Box>
            )}

            {detail.role === "patient" && (
              <>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                  Telehealth Orders ({detail.telehealth_orders.length})
                </Typography>
                {detail.telehealth_orders.length === 0 ? (
                  <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mb: 2 }}>None.</Typography>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    {detail.telehealth_orders.map((o) => (
                      <Box key={o._id} sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, py: 0.4 }}>
                        <span>{o.child_first_name} {o.child_last_name} — {o.customer_id}</span><b>{o.order_status}</b>
                      </Box>
                    ))}
                  </Box>
                )}

                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                  Pay It Forward ({detail.pay_it_forward_purchases.length})
                </Typography>
                {detail.pay_it_forward_purchases.length === 0 ? (
                  <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mb: 2 }}>None.</Typography>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    {detail.pay_it_forward_purchases.map((p) => (
                      <Box key={p._id} sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, py: 0.4 }}>
                        <span>{p.code} — {p.quantity_purchased} kits</span><b>{p.status}</b>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}

            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
              Recent Notifications
            </Typography>
            {detail.recent_notifications.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>None logged.</Typography>
            ) : (
              detail.recent_notifications.map((n) => (
                <Box key={n._id} sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, py: 0.4 }}>
                  <span>{n.kind} ({n.channel})</span>
                  <b style={{ color: n.status === "sent" ? "#16a34a" : "#dc2626" }}>{n.status}</b>
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
